<script setup lang="ts">
  import { BottomSheet, Button, ChipSize, IconName, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import {
    DestinationMobileChips,
    DestinationResortList,
    DestinationSearchInput,
    type DestinationChipItem,
  } from '@/features/searchForm/destination';
  import type { Resort } from '@/shared/model';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import { SERP_FILTERS_TEXTS, SERP_RESORTS_FILTER_TEXTS } from '../config/texts';

  interface ResortsFilterSheetProps {
    resorts: Resort[];
    selectedIds: number[];
    allLabel: string;
  }

  const props = defineProps<ResortsFilterSheetProps>();

  interface ResortsFilterSheetEmits {
    (e: 'apply', resortIds: number[]): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<ResortsFilterSheetEmits>();

  const SHEET_Z_INDEX = 10;
  const ALL_RESORTS_CHIP_KEY = 'all';

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  const query = ref<string>('');
  const checkedIds = ref<number[]>([...props.selectedIds]);

  const chipItems = computed<DestinationChipItem[]>(() => {
    if (!checkedIds.value.length) {
      return [{ key: ALL_RESORTS_CHIP_KEY, label: props.allLabel, removable: true, truncate: false }];
    }

    return props.resorts
      .filter((resort) => checkedIds.value.includes(resort.id))
      .map((resort) => ({ key: String(resort.id), label: resort.name, removable: true, truncate: true }));
  });

  const toggleResort = (resort: Resort): void => {
    checkedIds.value = checkedIds.value.includes(resort.id)
      ? checkedIds.value.filter((id) => id !== resort.id)
      : [...checkedIds.value, resort.id];
  };

  const selectAllResorts = (): void => {
    checkedIds.value = [];
  };

  const removeChip = (key: string): void => {
    checkedIds.value = checkedIds.value.filter((id) => String(id) !== key);
  };

  const close = async (): Promise<void> => {
    await bottomSheetRef.value?.close();
  };

  const onApply = async (): Promise<void> => {
    emit('apply', checkedIds.value);
    await close();
  };

  const onClose = (): void => {
    emit('close');
  };
</script>

<template>
  <div
    class="resorts-filter-sheet"
    data-testid="serp-resorts-filter-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="resorts-filter-sheet__root"
      content-class="resorts-filter-sheet__content"
      height="95dvh"
      :z-index="SHEET_Z_INDEX"
      :animated="true"
      @close="onClose"
    >
      <template #header>
        <div class="resorts-filter-sheet__header">
          <Button
            class="resorts-filter-sheet__back"
            type="icon"
            appearance="secondary"
            :icon="IconName.ChevronLeft"
            :aria-label="SERP_RESORTS_FILTER_TEXTS.back"
            data-testid="serp-resorts-filter-sheet-back"
            @click="close"
          />
          <Typo
            class="resorts-filter-sheet__title"
            :variant="TypoVariant.Heading5"
            tag="span"
            data-testid="serp-resorts-filter-sheet-title"
          >
            {{ SERP_RESORTS_FILTER_TEXTS.sheetTitle }}
          </Typo>
        </div>
      </template>

      <div
        class="resorts-filter-sheet__body"
        @touchmove.stop
      >
        <div class="resorts-filter-sheet__search">
          <DestinationSearchInput
            v-model="query"
            :placeholder="SERP_RESORTS_FILTER_TEXTS.searchPlaceholder"
          />
        </div>

        <DestinationMobileChips
          :items="chipItems"
          :size="ChipSize.S"
          @remove="removeChip"
        />

        <div class="resorts-filter-sheet__list">
          <DestinationResortList
            :resorts="resorts"
            :checked-ids="checkedIds"
            :all-label="allLabel"
            :all-checked="checkedIds.length === 0"
            :query="query"
            :group-titles="SERP_RESORTS_FILTER_TEXTS.groupTitles"
            :divided="false"
            @toggle="toggleResort"
            @toggle-all="selectAllResorts"
          />
        </div>
      </div>

      <template #footer>
        <MobilePrimaryButton
          testid="serp-resorts-filter-sheet-apply"
          @click="onApply"
        >
          {{ SERP_FILTERS_TEXTS.apply }}
        </MobilePrimaryButton>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .resorts-filter-sheet {
    display: contents;

    &__header {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
      padding-top: var(--spacing-xxs);
      width: 100%;

      &::after {
        content: '';
        flex: 0 0 var(--icon-m);
      }
    }

    &__back {
      flex: 0 0 auto;
    }

    &__title {
      flex: 1 1 auto;
      min-width: 0;
      text-align: center;
    }

    &__body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
    }

    &__search {
      flex: 0 0 auto;
      padding: 0 var(--spacing-m) var(--spacing-xs);
    }

    &__list {
      flex: 1 1 auto;
      min-height: 0;
      -webkit-overflow-scrolling: touch;
      overflow-y: auto;
    }
  }
</style>

<style lang="scss">
  .resorts-filter-sheet__root .resorts-filter-sheet__content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
  }
</style>
